import { type CacheAnalyticsCallback, promiseCache } from '../../../utils/caching/persist-refresh';
import { DegradablePromise, type DegradationCallback } from '../../utils/degradable-promise';

import { fetchOutboundAuthDataInternal } from './fetch-outbound-auth-data-internal';
import { type OutboundAuthContainerResponse } from './types';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { fetchOutboundAuthDataInternal } from './fetch-outbound-auth-data-internal';

const outboundAuthCache = promiseCache<OutboundAuthContainerResponse>({
	name: 'outbound-auth-cache',
	expiration: 1000 * 60 * 5, // 5 minutes
});

export type OutboundAuthFetchProps = {
	/**
	 * Atlassian Account ID
	 */
	aaid: string;

	/**
	 * Container ID (UUID)
	 */
	containerId: string;

	/**
	 * Service key to filter by (e.g., 'github')
	 */
	serviceKey: string;

	/**
	 * Determines if cached value should be refreshed if previous value has not expired yet
	 */
	onlyRefreshIfExpired?: boolean;

	/**
	 * Duration (in ms) that the result of this request should be cached for
	 */
	expiration?: number;

	/**
	 * Callback to execute when API is too slow and must degrade
	 */
	degradationCallback?: DegradationCallback;

	/**
	 * Callback to execute with outcome of cache operation for analytics
	 */
	cacheAnalyticsCallback?: CacheAnalyticsCallback;

	/**
	 * If true, the cached value will be returned even if it has expired
	 */
	returnExpired?: boolean;
};

/**
 * Fetches outbound auth data for a given user and container with caching
 * @param props - Configuration object with aaid, containerId, serviceKey and optional callbacks
 * @returns Promise<OutboundAuthContainerResponse> - The API response
 */
export const fetchOutboundAuthData = async ({
	aaid,
	containerId,
	serviceKey,
	onlyRefreshIfExpired = true,
	expiration,
	degradationCallback = () => {},
	cacheAnalyticsCallback,
	returnExpired = false,
}: OutboundAuthFetchProps): Promise<OutboundAuthContainerResponse> => {
	const supplier: () => Promise<OutboundAuthContainerResponse> = () => {
		const outboundAuthPromise = fetchOutboundAuthDataInternal(aaid, containerId, serviceKey);

		const degrader = new DegradablePromise<OutboundAuthContainerResponse>(
			outboundAuthPromise,
			degradationCallback,
		);

		return degrader.promise();
	};

	const cacheKey = `${aaid}-${containerId}-${serviceKey}`;

	return outboundAuthCache.refreshOnAccessDecorator({
		key: cacheKey,
		supplier,
		returnExpired,
		onlyRefreshIfExpired,
		expiration,
		analyticsCallback: cacheAnalyticsCallback,
	});
};
